import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, PackageX, Boxes, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (cents) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

const readLimit = (key, fallback) => {
  const v = parseInt(localStorage.getItem(key), 10);
  return Number.isFinite(v) ? v : fallback;
};

function LimitInput({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wide">
      {label}
      <input type="number" min="0" value={value} onChange={(e) => onChange(Math.max(0, parseInt(e.target.value, 10) || 0))}
        data-testid={testId}
        className="hard-shadow w-20 rounded-lg border-2 border-[#0A0A0A] px-2.5 py-1.5 text-sm font-bold outline-none focus:ring-4 focus:ring-[#FF2A54]/40" />
    </label>
  );
}

function RestockRow({ p, onSaved }) {
  const [stock, setStock] = useState(String(p.stock));
  const [saving, setSaving] = useState(false);
  useEffect(() => setStock(String(p.stock)), [p.stock]);
  const dirty = (parseInt(stock, 10) || 0) !== p.stock;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/products/${p.id}`, { stock: parseInt(stock, 10) || 0 }, { withCredentials: true });
      toast.success(`${p.name}: stock set to ${parseInt(stock, 10) || 0}`);
      onSaved();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr data-testid={`stock-row-${p.slug}`} className="border-b-2 border-[#0A0A0A]/10 last:border-0">
      <td className="py-2.5 pl-4 pr-3">
        <img src={p.image} alt="" className="h-10 w-10 rounded-lg border-2 border-[#0A0A0A] object-cover" />
      </td>
      <td className="px-3 py-2.5">
        <p className="font-display text-sm font-bold leading-tight">{p.name}</p>
        {p.sku && <span className="text-[10px] font-bold uppercase text-[#4A4A4A]">SKU {p.sku}</span>}
      </td>
      <td className="px-3 py-2.5">
        <span className="rounded-full border-2 border-[#0A0A0A] bg-[#FFD700] px-2.5 py-0.5 text-[10px] font-bold uppercase whitespace-nowrap">{p.tag}</span>
      </td>
      <td className="px-3 py-2.5 text-sm font-bold">{fmt(p.price_cents)}</td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <input type="number" min="0" step="1" value={stock} onChange={(e) => setStock(e.target.value)}
            data-testid={`stock-input-${p.slug}`}
            className="hard-shadow w-20 rounded-lg border-2 border-[#0A0A0A] px-2.5 py-1.5 text-sm font-bold outline-none focus:ring-4 focus:ring-[#FF2A54]/40" />
          {dirty && (
            <button onClick={save} disabled={saving} data-testid={`stock-save-${p.slug}`}
              className="hard-shadow hard-shadow-hover inline-flex items-center gap-1 rounded-full border-2 border-[#0A0A0A] bg-[#22c55e] px-3 py-1.5 text-xs font-extrabold uppercase text-white disabled:opacity-60">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function Section({ title, icon: Icon, color, items, empty, testId, onSaved }) {
  return (
    <section data-testid={testId} className="hard-shadow overflow-hidden rounded-2xl border-2 border-[#0A0A0A] bg-white">
      <div className={`flex items-center justify-between border-b-2 border-[#0A0A0A] px-5 py-3 ${color}`}>
        <h3 className="inline-flex items-center gap-2 font-display text-base font-extrabold uppercase tracking-tight">
          <Icon className="h-4 w-4" /> {title}
        </h3>
        <span data-testid={`${testId}-count`} className="rounded-full border-2 border-[#0A0A0A] bg-white px-3 py-0.5 text-xs font-extrabold">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="p-6 text-center text-sm font-medium text-[#4A4A4A]">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-left">
            <tbody>{items.map((p) => <RestockRow key={p.id} p={p} onSaved={onSaved} />)}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function StockCheck({ products, onSaved }) {
  const [min, setMin] = useState(() => readLimit("cb_stock_min", 3));
  const [max, setMax] = useState(() => readLimit("cb_stock_max", 50));
  useEffect(() => localStorage.setItem("cb_stock_min", String(min)), [min]);
  useEffect(() => localStorage.setItem("cb_stock_max", String(max)), [max]);

  const byCat = (a, b) => a.tag.localeCompare(b.tag) || a.name.localeCompare(b.name);
  const soldOut = products.filter((p) => p.stock === 0).sort(byCat);
  const low = products.filter((p) => p.stock > 0 && p.stock <= min).sort((a, b) => a.stock - b.stock || byCat(a, b));
  const over = products.filter((p) => p.stock >= max && max > 0).sort((a, b) => b.stock - a.stock || byCat(a, b));
  const units = products.reduce((s, p) => s + p.stock, 0);
  const retail = products.reduce((s, p) => s + p.stock * p.price_cents, 0);
  const cost = products.reduce((s, p) => s + p.stock * (p.cost_cents || 0), 0);

  return (
    <div data-testid="stock-check" className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">
          Stock <span className="text-[#FF2A54]">Check</span>
        </h2>
        <div className="flex flex-wrap items-center gap-4">
          <LimitInput label="Low at ≤" value={min} onChange={setMin} testId="stock-min-input" />
          <LimitInput label="Overstock at ≥" value={max} onChange={setMax} testId="stock-max-input" />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-4" data-testid="stock-summary">
        {[
          ["Products", products.length], ["Units on hand", units],
          ["Retail value", fmt(retail)], ["Cost on hand", cost ? fmt(cost) : "—"],
        ].map(([label, value]) => (
          <div key={label} className="hard-shadow rounded-2xl border-2 border-[#0A0A0A] bg-white px-4 py-3">
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-[#4A4A4A]">{label}</p>
            <p className="font-display text-xl font-extrabold">{value}</p>
          </div>
        ))}
      </div>

      <Section title="Sold out" icon={PackageX} color="bg-[#FF2A54] text-white" items={soldOut}
        empty="Nothing is sold out. Nice." testId="stock-soldout" onSaved={onSaved} />
      <Section title={`Low stock (${min} or fewer)`} icon={AlertTriangle} color="bg-[#FFD700]" items={low}
        empty="No items running low." testId="stock-low" onSaved={onSaved} />
      <Section title={`Overstock (${max}+)`} icon={Boxes} color="bg-[#FFF7DB]" items={over}
        empty="Nothing over your max." testId="stock-over" onSaved={onSaved} />
    </div>
  );
}
